import styles from './shimmer.module.css'

/* ─── Generic line ─────────────────────────────────────────────── */
export function ShimmerLine({ width = 'med' }: { width?: 'short' | 'med' | 'long' | 'full' }) {
  const widthClass = {
    short: styles.lineShort,
    med:   styles.lineMed,
    long:  styles.lineLong,
    full:  styles.lineFull,
  }[width]
  return <div className={`${styles.line} ${widthClass}`} aria-hidden />
}

/* ─── Table shimmer rows ───────────────────────────────────────── */
type TableRowProps = { cols: number; rows?: number }

export function ShimmerTableRows({ cols, rows = 5 }: TableRowProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className={styles.tableRow} aria-hidden>
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c}>
              {c === cols - 2 ? (
                /* Second-to-last col typically a status badge */
                <div className={styles.tableCellBadge} />
              ) : c === cols - 1 ? (
                /* Last col is often a button — make it shorter */
                <div className={styles.tableCellShort} />
              ) : (
                <div className={styles.tableCell} />
              )}
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

/* ─── Card shimmer ─────────────────────────────────────────────── */
export function ShimmerCard({ compact = false }: { compact?: boolean }) {
  return <div className={compact ? styles.cardCompact : styles.card} aria-hidden />
}

/* ─── Card list ────────────────────────────────────────────────── */
export function ShimmerCardList({ count = 3, compact = false }: { count?: number; compact?: boolean }) {
  return (
    <div className={styles.pageShell} aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <ShimmerCard key={i} compact={compact} />
      ))}
    </div>
  )
}

/* ─── Transactions page skeleton ───────────────────────────────── */
export function ShimmerTransactionsPage({ cols = 6 }: { cols?: number }) {
  return (
    <div className={styles.pageShell} aria-label="Loading transactions…">
      {/* Header + filters */}
      <div className={styles.headerBlock} aria-hidden />
      <div className={styles.filterRow} aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.filterChip} />
        ))}
      </div>

      {/* Table skeleton — matches real table structure */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-hidden>
        <tbody>
          <ShimmerTableRows cols={cols} rows={6} />
        </tbody>
      </table>
    </div>
  )
}

/* ─── Dashboard page skeleton ──────────────────────────────────── */
export function ShimmerDashboardPage() {
  return (
    <div className={styles.dashboardShell} aria-label="Loading dashboard…">
      <div className={styles.bannerBlock} aria-hidden />
      <div className={styles.cardRow} aria-hidden>
        <div className={styles.dashCard} />
        <div className={styles.dashCard} />
        <div className={styles.dashCard} />
      </div>
      <div className={styles.sectionTitle} aria-hidden />
      <div className={styles.questionRow} aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.questionItem} />
        ))}
      </div>
    </div>
  )
}

/* ─── Candidates stat number shimmer ─────────────────────────────── */
export function ShimmerStatNum() {
  return <div className={styles.shimmerStatNum} aria-hidden />
}

/* ─── Recruiters (Team) page skeleton ────────────────────────────── */
export function ShimmerRecruitersPage() {
  return (
    <div className={styles.teamShimmerShell} aria-label="Loading team…">
      {/* Header */}
      <div className={styles.teamShimmerHeader} aria-hidden>
        <div className={styles.teamShimmerHeaderLeft}>
          <div className={styles.teamShimmerTitle} />
          <div className={styles.teamShimmerSubtitle} />
        </div>
        <div className={styles.teamShimmerBtn} />
      </div>

      {/* Stats row — 4 vertical stat cards */}
      <div className={styles.teamShimmerStatsRow} aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.teamShimmerStatCard}>
            <div className={styles.teamShimmerStatIcon} />
            <div className={styles.teamShimmerStatNum} />
            <div className={styles.teamShimmerStatLabel} />
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className={styles.teamShimmerFilters} aria-hidden>
        <div className={styles.teamShimmerSearch} />
        <div className={styles.teamShimmerSelect} />
        <div className={styles.teamShimmerSelect} />
        <div className={styles.teamShimmerSelect} />
      </div>

      {/* Table shimmer */}
      <div className={styles.teamShimmerTableArea} aria-hidden>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <ShimmerTableRows cols={7} rows={6} />
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─── Job Roles inline stats shimmer ─────────────────────────────── */
export function ShimmerJobRolesStats() {
  return (
    <div className={styles.jrShimmerStats} aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={styles.jrShimmerStatItem}>
          <div className={styles.jrShimmerStatDot} />
          <div className={styles.jrShimmerStat} />
        </div>
      ))}
    </div>
  )
}

const JR_ACTION_BTN_WIDTHS = [130, 110, 110]

/* ─── Job Roles page skeleton ────────────────────────────────────── */
export function ShimmerJobRolesPage({ showHeader = true }: { showHeader?: boolean }) {
  const list = (
    <div className={styles.jrShimmerCardList} aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={styles.jrShimmerCard}>
          <div className={styles.jrShimmerCardTop}>
            <div className={styles.jrShimmerAvatar} />
            <div className={styles.jrShimmerTitleBlock}>
              <div className={styles.jrShimmerName} />
              <div className={styles.jrShimmerDate} />
            </div>
            <div className={styles.jrShimmerTopRight}>
              <div className={styles.jrShimmerBadge} />
              {i % 3 === 0 && <div className={styles.jrShimmerStatusPill} />}
            </div>
          </div>
          {i % 2 === 0 && <div className={styles.jrShimmerMetaLine} />}
          <div className={styles.jrShimmerDivider} />
          <div className={styles.jrShimmerBottomRow}>
            <ShimmerJobRolesStats />
            <div className={styles.jrShimmerActions}>
              {JR_ACTION_BTN_WIDTHS.map((width, j) => (
                <div key={j} className={styles.jrShimmerActionBtn} style={{ width }} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  if (!showHeader) return list

  return (
    <div className={styles.jrShimmerShell} aria-label="Loading job roles…">
      <div className={styles.jrShimmerHeader} aria-hidden>
        <div className={styles.jrShimmerHeaderLeft}>
          <div className={styles.jrShimmerTitle} />
          <div className={styles.jrShimmerSubtitle} />
        </div>
        <div className={styles.jrShimmerHeaderBtns}>
          <div className={styles.jrShimmerBtn} />
        </div>
      </div>
      <div className={styles.jrShimmerTabsRow} aria-hidden>
        <div className={styles.jrShimmerTabsPill}>
          <div className={styles.jrShimmerTabChip} style={{ width: 60 }} />
          <div className={styles.jrShimmerTabChip} style={{ width: 90 }} />
          <div className={styles.jrShimmerTabChip} style={{ width: 100 }} />
        </div>
        <div className={styles.jrShimmerSearchBar} />
      </div>
      {list}
    </div>
  )
}

/* ─── Workflow Template page skeleton ──────────────────────────── */
export function ShimmerWorkflowTemplatePage() {
  return (
    <div className={styles.wfShell} aria-label="Loading workflow template…">
      {/* Page header */}
      <div className={styles.wfPageHeader}>
        <div className={styles.wfPageHeaderLeft}>
          <div className={styles.wfTitleRow}>
            <div className={styles.wfTitleBlock}>
              <div className={styles.wfTitle} aria-hidden />
              <div className={styles.wfSubtitle} aria-hidden />
            </div>
            <div className={styles.wfHeaderBtn} aria-hidden />
          </div>
          <div className={styles.wfBadgesRow} aria-hidden>
            <div className={styles.wfBadge} />
            <div className={styles.wfBadge} />
          </div>
        </div>
      </div>

      {/* Info banner */}
      <div className={styles.wfBanner} aria-hidden />

      {/* Content grid */}
      <div className={styles.wfContentGrid} aria-hidden>
        {/* Main column: workflow card */}
        <div className={styles.wfWorkflowCard}>
          <div className={styles.wfCardHeader}>
            <div className={styles.wfCardHeaderText}>
              <div className={styles.wfCardTitle} />
              <div className={styles.wfCardSubtitle} />
            </div>
            <div className={styles.wfReadOnlyBadge} />
          </div>
          <div className={styles.wfCardDivider} />
          <div className={styles.wfStageList}>
            {[0, 1, 2].map((stageIndex) => (
              <div key={stageIndex} className={styles.wfStageRow}>
                <div className={styles.wfStageCircle} />
                <div className={styles.wfStageBody}>
                  <div className={styles.wfStageName} />
                  <div className={styles.wfStageMeta} />
                </div>
                <div className={styles.wfStagePositionBadge} />
              </div>
            ))}
          </div>
          <div className={styles.wfLockedFooter} />
        </div>

        {/* Sidebar: summary card */}
        <div className={styles.wfSummaryCard}>
          <div className={styles.wfSummaryTitle} />
          <div className={styles.wfSummaryDivider} />
          <div className={styles.wfSummaryRows}>
            {[0, 1, 2].map((rowIndex) => (
              <div key={rowIndex} className={styles.wfSummaryRow}>
                <div className={styles.wfSummaryIcon} />
                <div className={styles.wfSummaryLabel} />
                <div className={styles.wfSummaryValue} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Enterprise Dashboard page skeleton ─────────────────────────── */
export function ShimmerEnterpriseDashboard() {
  return (
    <div className={styles.entDashboardShell} aria-label="Loading dashboard…">
      {/* Welcome Header Shimmer */}
      <div className={styles.entHeaderBlock} aria-hidden>
        <div className={styles.entTitleBlock} />
        <div className={styles.entSubTitleBlock} />
      </div>

      {/* KPI Cards Row Shimmer */}
      <div className={styles.entKpiRow} aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.entKpiCard}>
            <div className={styles.entKpiCardTop}>
              <div className={styles.entKpiIcon} />
              <div className={styles.entKpiTrend} />
            </div>
            <div className={styles.entKpiNumber} />
            <div className={styles.entKpiLabel} />
          </div>
        ))}
      </div>

      {/* Workflow Card Shimmer */}
      <div className={styles.entPipelineCard} aria-hidden>
        <div className={styles.entWorkflowHeader}>
          <div className={styles.entWorkflowTitleGroup}>
            <div className={styles.entWorkflowTitle} />
            <div className={styles.entWorkflowSubtitle} />
          </div>
          <div className={styles.entWorkflowDropdown} />
        </div>
        <div className={styles.entWorkflowStagesGrid}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={styles.entWorkflowStage}>
              <div className={styles.entStageHeader}>
                <div className={styles.entStageTitle} />
                <div className={styles.entStageCount} />
              </div>
              <div className={styles.entStageTrack} />
              <div className={styles.entStageActivity} />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Grid: Recent Activity + Weekly Chart */}
      <div className={styles.entBottomRow} aria-hidden>
        {/* Recent Activity Card Shimmer */}
        <div className={styles.entActivityCard}>
          <div className={styles.entCardTitle} />
          <div className={styles.entActivityList}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={styles.entActivityItem}>
                <div className={styles.entActivityCircle} />
                <div className={styles.entActivityBody}>
                  <div className={styles.entActivityLine} style={{ width: "80%" }} />
                  <div className={styles.entActivityLine} style={{ width: "50%", height: "10px", marginTop: "6px" }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly Chart Card Shimmer */}
        <div className={styles.entWeeklyCard}>
          <div className={styles.entWeeklyHeader}>
            <div className={styles.entWeeklyTitleGroup}>
              <div className={styles.entWeeklyTitle} />
              <div className={styles.entWeeklySubtitle} />
            </div>
            <div className={styles.entWeeklyTrend} />
          </div>
          <div className={styles.entWeeklyMetrics}>
            <div className={styles.entWeeklyMetricBlock} />
            <div className={styles.entWeeklyMetricBlock} />
          </div>
          <div className={styles.entChartArea}>
            <div className={styles.entChartYAxis}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={styles.entChartYLabel} />
              ))}
            </div>
            <div className={styles.entChartBars}>
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={styles.entChartDayGroup}>
                  <div className={styles.entChartDayBars}>
                    <div className={styles.entChartBar} style={{ height: `${20 + i * 10}%` }} />
                    <div className={styles.entChartBar} style={{ height: `${10 + i * 12}%` }} />
                  </div>
                  <div className={styles.entChartDayLabel} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

